import { createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

import { onMounted, onBeforeUnmount, ref } from "vue";
import CourseAppointmentModal from "./components/CourseAppointmentModal.vue";
import CourseAppointmentConflicts from "./components/CourseAppointmentConflicts.vue";
import SeriesActionChoiceDialog from "../../../room-booking-management/calendar/components/SeriesActionChoiceDialog.vue";
import AppointmentCopyModal from "./components/AppointmentCopyModal.vue";
import AppointmentBookingModal from "./components/AppointmentBookingModal.vue";
import ConfirmationDialog from "../../../room-booking-management/calendar/components/ConfirmationDialog.vue";
import ConflictConfirmModal from "./components/ConflictConfirmModal.vue";
import {
	createInstance,
	createSeries,
	updateInstance,
	updateSeries,
	deleteInstance,
	deleteSeries,
	unbookAppointment,
	checkConflicts,
} from "./api";


export default {
  __name: 'CourseAppointmentApp',
  props: {
	apiUrl: { type: String, required: true },
	lecturePeriodStart: { type: String, default: null },
	lecturePeriodEnd: { type: String, default: null },
	employeeSearchApiUrl: { type: String, default: null },
	defaultTitle: { type: String, default: "" },
	defaultEmployeeId: { type: Number, default: null },
	defaultEmployeeName: { type: String, default: "" },
	nonTeachingDates: { type: Object, default: () => ({}) },
},
  setup(__props) {

const props = __props;

const modalShow = ref(false);
const modalMode = ref("create");
const currentAppointment = ref(null);
const currentSeries = ref(null);
const saving = ref(false);

const choiceShow = ref(false);
const choiceAction = ref("edit");
const choiceBooking = ref(null);

// Conflict confirmation (room / lecturer / cohort) shown as a table before saving.
const conflictShow = ref(false);
const conflictList = ref([]);
let conflictConfirmAction = null;

const copyShow = ref(false);
const bookShow = ref(false);

// Bootstrap confirmation dialog (replaces native window.confirm).
const confirmShow = ref(false);
const confirmConfig = ref({
	title: "Bestätigung erforderlich",
	message: "",
	confirmText: "Bestätigen",
	confirmButtonClass: "btn btn-primary",
	icon: "",
});
let confirmAction = null;

function askConfirm(config, action) {
	confirmConfig.value = {
		title: config.title || "Bestätigung erforderlich",
		message: config.message || "Möchten Sie fortfahren?",
		confirmText: config.confirmText || "Bestätigen",
		confirmButtonClass: config.confirmButtonClass || "btn btn-primary",
		icon: config.icon || "",
	};
	confirmAction = action;
	confirmShow.value = true;
}

async function onConfirmDialog() {
	confirmShow.value = false;
	const action = confirmAction;
	confirmAction = null;
	if (action) {
		await action();
	}
}

function onConfirmCancel() {
	confirmShow.value = false;
	confirmAction = null;
}

function closeModal() {
	modalShow.value = false;
}

function onCopied() {
	window.location.reload();
}

function onBooked() {
	window.location.reload();
}

function openUnbook(context, entity) {
	if (!entity) return;

	const payload = context === "series" ? { seriesId: entity.id } : { appointmentId: entity.id };
	const message =
		context === "series"
			? "Soll die Buchung dieser Terminreihe aufgehoben werden? Die Raumbuchungen werden " +
				"storniert und die Termine zurück in die Planung verschoben."
			: "Soll die Buchung dieses Termins aufgehoben werden? Die Raumbuchung wird " +
				"storniert und der Termin zurück in die Planung verschoben.";

	askConfirm(
		{
			title: "Buchung aufheben",
			message,
			confirmText: "Buchung aufheben",
			confirmButtonClass: "btn btn-warning",
			icon: "warning",
		},
		() => runDelete(() => unbookAppointment(props.apiUrl, payload))
	);
}

function parseEntity(el) {
	try {
		return JSON.parse(el.getAttribute("data-entity") || "null");
	} catch (error) {
		console.error("Invalid appointment payload", error);
		return null;
	}
}

function openCreate() {
	currentAppointment.value = null;
	currentSeries.value = null;
	modalMode.value = "create";
	modalShow.value = true;
}

function openEdit(context, entity) {
	if (!entity) return;

	if (context === "series") {
		currentSeries.value = entity;
		currentAppointment.value = null;
		modalMode.value = "edit-series";
		modalShow.value = true;
	} else if (context === "standalone") {
		currentAppointment.value = entity;
		currentSeries.value = null;
		modalMode.value = "edit";
		modalShow.value = true;
	} else if (context === "occurrence") {
		choiceBooking.value = entity;
		choiceAction.value = "edit";
		choiceShow.value = true;
	}
}

function openDelete(context, entity) {
	if (!entity) return;

	if (context === "series") {
		askConfirm(
			{
				title: "Terminreihe löschen",
				message: "Soll die gesamte Terminreihe gelöscht werden?",
				confirmText: "Löschen",
				confirmButtonClass: "btn btn-danger",
				icon: "danger",
			},
			() => runDelete(() => deleteSeries(props.apiUrl, entity.id))
		);
	} else if (context === "standalone") {
		askConfirm(
			{
				title: "Termin löschen",
				message: "Soll dieser Termin gelöscht werden?",
				confirmText: "Löschen",
				confirmButtonClass: "btn btn-danger",
				icon: "danger",
			},
			() => runDelete(() => deleteInstance(props.apiUrl, entity.id))
		);
	} else if (context === "occurrence") {
		choiceBooking.value = entity;
		choiceAction.value = "delete";
		choiceShow.value = true;
	}
}

async function onChoiceConfirm({ scope }) {
	const booking = choiceBooking.value;
	choiceShow.value = false;
	if (!booking) return;

	if (choiceAction.value === "edit") {
		if (scope === "instance") {
			currentAppointment.value = booking;
			currentSeries.value = null;
			modalMode.value = "edit-instance";
		} else {
			currentSeries.value = booking.series;
			currentAppointment.value = null;
			modalMode.value = "edit-series";
		}
		modalShow.value = true;
	} else {
		if (scope === "instance") {
			await runDelete(() => deleteInstance(props.apiUrl, booking.id));
		} else {
			await runDelete(() => deleteSeries(props.apiUrl, booking.series.id));
		}
	}
}

async function onModalSubmit({ action, data }) {
	// Planning-stage conflict pre-check (room / lecturer / cohort). A conflict is only a warning,
	// never a hard block; a failing check must not prevent saving.
	let conflictResult = null;
	try {
		const checkPayload = { ...data };
		if (action === "update-instance") checkPayload.excludeId = data.id;
		if (action === "update-series") checkPayload.excludeSeriesId = data.id;
		conflictResult = await checkConflicts(props.apiUrl, checkPayload);
	} catch (error) {
		console.error("Konfliktprüfung fehlgeschlagen", error);
	}

	if (conflictResult?.conflicts?.length) {
		conflictList.value = conflictResult.conflicts;
		conflictConfirmAction = () => performSave(action, data);
		conflictShow.value = true;
		return;
	}

	await performSave(action, data);
}

function onConflictConfirm() {
	conflictShow.value = false;
	const action = conflictConfirmAction;
	conflictConfirmAction = null;
	if (action) {
		action();
	}
}

function onConflictCancel() {
	conflictShow.value = false;
	conflictConfirmAction = null;
}

async function performSave(action, data) {
	saving.value = true;
	try {
		switch (action) {
			case "create-instance":
				await createInstance(props.apiUrl, data);
				break;
			case "create-series":
				await createSeries(props.apiUrl, data);
				break;
			case "update-instance":
				await updateInstance(props.apiUrl, data);
				break;
			case "update-series":
				await updateSeries(props.apiUrl, data);
				break;
		}
		window.location.reload();
	} catch (error) {
		saving.value = false;
		window.alert(`Speichern fehlgeschlagen: ${error.message}`);
	}
}


async function runDelete(action) {
	saving.value = true;
	try {
		await action();
		window.location.reload();
	} catch (error) {
		saving.value = false;
		window.alert(`Löschen fehlgeschlagen: ${error.message}`);
	}
}

function onDocumentClick(event) {
	const addBtn = event.target.closest(".js-ca-add");
	if (addBtn) {
		event.preventDefault();
		openCreate();
		return;
	}

	const copyBtn = event.target.closest(".js-ca-copy");
	if (copyBtn) {
		event.preventDefault();
		copyShow.value = true;
		return;
	}

	const bookBtn = event.target.closest(".js-ca-book");
	if (bookBtn) {
		event.preventDefault();
		bookShow.value = true;
		return;
	}

	const unbookBtn = event.target.closest(".js-ca-unbook");
	if (unbookBtn) {
		event.preventDefault();
		openUnbook(unbookBtn.getAttribute("data-context"), parseEntity(unbookBtn));
		return;
	}

	const editBtn = event.target.closest(".js-ca-edit");
	if (editBtn) {
		event.preventDefault();
		openEdit(editBtn.getAttribute("data-context"), parseEntity(editBtn));
		return;
	}

	const deleteBtn = event.target.closest(".js-ca-delete");
	if (deleteBtn) {
		event.preventDefault();
		openDelete(deleteBtn.getAttribute("data-context"), parseEntity(deleteBtn));
	}
}

onMounted(() => document.addEventListener("click", onDocumentClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocumentClick));

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(CourseAppointmentConflicts, { "api-url": __props.apiUrl }, null, 8 /* PROPS */, ["api-url"]),
    _createVNode(CourseAppointmentModal, {
      show: modalShow.value,
      mode: modalMode.value,
      appointment: currentAppointment.value,
      series: currentSeries.value,
      saving: saving.value,
      "lecture-period-start": __props.lecturePeriodStart,
      "lecture-period-end": __props.lecturePeriodEnd,
      "employee-search-api-url": __props.employeeSearchApiUrl,
      "default-title": __props.defaultTitle,
      "default-employee-id": __props.defaultEmployeeId,
      "default-employee-name": __props.defaultEmployeeName,
      "non-teaching-dates": __props.nonTeachingDates,
      onClose: closeModal,
      onSubmit: onModalSubmit
    }, null, 8 /* PROPS */, ["show", "mode", "appointment", "series", "saving", "lecture-period-start", "lecture-period-end", "employee-search-api-url", "default-title", "default-employee-id", "default-employee-name", "non-teaching-dates"]),
    (choiceBooking.value)
      ? (_openBlock(), _createBlock(SeriesActionChoiceDialog, {
          key: 0,
          show: choiceShow.value,
          action: choiceAction.value,
          booking: choiceBooking.value,
          onClose: _cache[0] || (_cache[0] = $event => (choiceShow.value = false)),
          onConfirm: onChoiceConfirm
        }, null, 8 /* PROPS */, ["show", "action", "booking"]))
      : _createCommentVNode("v-if", true),
    _createVNode(AppointmentCopyModal, {
      show: copyShow.value,
      "api-url": __props.apiUrl,
      onClose: _cache[1] || (_cache[1] = $event => (copyShow.value = false)),
      onCopied: onCopied
    }, null, 8 /* PROPS */, ["show", "api-url"]),
    _createVNode(AppointmentBookingModal, {
      show: bookShow.value,
      "api-url": __props.apiUrl,
      onClose: _cache[2] || (_cache[2] = $event => (bookShow.value = false)),
      onBooked: onBooked
    }, null, 8 /* PROPS */, ["show", "api-url"]),
    _createVNode(ConfirmationDialog, {
      show: confirmShow.value,
      title: confirmConfig.value.title,
      message: confirmConfig.value.message,
      "confirm-text": confirmConfig.value.confirmText,
      "confirm-button-class": confirmConfig.value.confirmButtonClass,
      icon: confirmConfig.value.icon,
      onClose: onConfirmCancel,
      onConfirm: onConfirmDialog
    }, null, 8 /* PROPS */, ["show", "title", "message", "confirm-text", "confirm-button-class", "icon"]),
    _createVNode(ConflictConfirmModal, {
      show: conflictShow.value,
      conflicts: conflictList.value,
      onClose: onConflictCancel,
      onConfirm: onConflictConfirm
    }, null, 8 /* PROPS */, ["show", "conflicts"])
  ]))
}
}

}